const STATE_KEY = "hydrotools:bag-s102-converter:v1";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function readState() {
  const fallback = {
    inputBag: "",
    outputS102: "",
    edition: "auto",
    datum: "MLLW",
    targetCrs: "",
    bbox: "",
    xRes: "",
    yRes: "",
    metadataJson: "",
    overwrite: true,
    validate: true,
    dryRun: false,
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { ...fallback, ...(parsed && typeof parsed === "object" ? parsed : {}) };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function quoteArg(v) {
  const s = String(v || "").trim();
  if (!s) return "\"\"";
  const escaped = s.replaceAll("\"", "`\"");
  return `"${escaped}"`;
}

function buildCommand(state) {
  const args = [
    "python",
    "scripts\\bag2s102.py",
    quoteArg(state.inputBag),
    quoteArg(state.outputS102),
    "--edition",
    state.edition || "auto",
    "--datum",
    quoteArg(state.datum || "MLLW"),
  ];

  if (String(state.targetCrs || "").trim()) args.push("--target-crs", quoteArg(state.targetCrs));
  if (String(state.bbox || "").trim()) args.push("--bbox", quoteArg(state.bbox));
  if (String(state.xRes || "").trim()) args.push("--x-res", String(state.xRes).trim());
  if (String(state.yRes || "").trim()) args.push("--y-res", String(state.yRes).trim());
  if (String(state.metadataJson || "").trim()) args.push("--metadata-json", quoteArg(state.metadataJson));
  if (state.overwrite) args.push("--overwrite");
  if (!state.validate) args.push("--no-validate");
  if (state.dryRun) args.push("--dry-run");

  return args.join(" ");
}

function downloadText(filename, text) {
  const blob = new Blob([String(text || "")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export const bagS102ConverterTool = {
  id: "bag-s102-converter",
  name: "BAG -> S-102",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let status = "";

  function persist() {
    writeState(state);
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    state.inputBag = String(containerEl.querySelector("#bag2s102-input")?.value || "");
    state.outputS102 = String(containerEl.querySelector("#bag2s102-output")?.value || "");
    state.edition = String(containerEl.querySelector("#bag2s102-edition")?.value || "auto");
    state.datum = String(containerEl.querySelector("#bag2s102-datum")?.value || "MLLW");
    state.targetCrs = String(containerEl.querySelector("#bag2s102-crs")?.value || "");
    state.bbox = String(containerEl.querySelector("#bag2s102-bbox")?.value || "");
    state.xRes = String(containerEl.querySelector("#bag2s102-xres")?.value || "");
    state.yRes = String(containerEl.querySelector("#bag2s102-yres")?.value || "");
    state.metadataJson = String(containerEl.querySelector("#bag2s102-meta")?.value || "");
    state.overwrite = !!containerEl.querySelector("#bag2s102-overwrite")?.checked;
    state.validate = !!containerEl.querySelector("#bag2s102-validate")?.checked;
    state.dryRun = !!containerEl.querySelector("#bag2s102-dryrun")?.checked;
    persist();
  }

  function validateForm() {
    if (!String(state.inputBag || "").trim()) return "Input BAG path is required.";
    if (!String(state.outputS102 || "").trim()) return "Output S-102 path is required.";
    if (state.bbox && !/^[-+0-9.eE]+,[-+0-9.eE]+,[-+0-9.eE]+,[-+0-9.eE]+$/.test(state.bbox.replaceAll(" ", ""))) {
      return "BBox must be minx,miny,maxx,maxy.";
    }
    return "";
  }

  function render() {
    if (!containerEl) return;
    const command = buildCommand(state);
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">BAG to S-102 Converter</div>
        <div class="small" style="margin-top:6px;">
          UI for generating the local conversion command. Run it in PowerShell/terminal where GDAL + s100py are installed.
        </div>

        <div class="row lm-add-row" style="margin-top:8px;">
          <label class="small" style="min-width:72px;">Input BAG</label>
          <input id="bag2s102-input" type="text" value="${esc(state.inputBag)}" placeholder="C:\\data\\survey.bag" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:72px;">Output</label>
          <input id="bag2s102-output" type="text" value="${esc(state.outputS102)}" placeholder="C:\\data\\survey.s102.h5" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:72px;">Edition</label>
          <select id="bag2s102-edition" style="width:120px;">
            <option value="auto"${state.edition === "auto" ? " selected" : ""}>auto</option>
            <option value="2.2"${state.edition === "2.2" ? " selected" : ""}>2.2</option>
            <option value="3.0"${state.edition === "3.0" ? " selected" : ""}>3.0</option>
          </select>
          <label class="small" style="min-width:52px;">Datum</label>
          <input id="bag2s102-datum" type="text" value="${esc(state.datum)}" style="width:110px;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:72px;">Target CRS</label>
          <input id="bag2s102-crs" type="text" value="${esc(state.targetCrs)}" placeholder="EPSG:4326 (optional)" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:72px;">BBox</label>
          <input id="bag2s102-bbox" type="text" value="${esc(state.bbox)}" placeholder="-123.2,37.5,-122.2,38.1 (optional)" style="flex:1;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:72px;">Res X/Y</label>
          <input id="bag2s102-xres" type="text" value="${esc(state.xRes)}" placeholder="x-res" style="width:120px;" />
          <input id="bag2s102-yres" type="text" value="${esc(state.yRes)}" placeholder="y-res" style="width:120px;" />
        </div>
        <div class="row lm-add-row" style="margin-top:6px;">
          <label class="small" style="min-width:72px;">Report JSON</label>
          <input id="bag2s102-meta" type="text" value="${esc(state.metadataJson)}" placeholder="tmp\\bag2s102-report.json (optional)" style="flex:1;" />
        </div>

        <div class="row lm-add-row" style="margin-top:8px;">
          <label class="small"><input id="bag2s102-overwrite" type="checkbox" ${state.overwrite ? "checked" : ""} /> overwrite</label>
          <label class="small"><input id="bag2s102-validate" type="checkbox" ${state.validate ? "checked" : ""} /> validate</label>
          <label class="small"><input id="bag2s102-dryrun" type="checkbox" ${state.dryRun ? "checked" : ""} /> dry-run</label>
        </div>

        <div class="lm-title" style="margin-top:10px;">Command</div>
        <textarea id="bag2s102-cmd" readonly style="width:100%; min-height:90px; resize:vertical;">${esc(command)}</textarea>
        <div class="row lm-add-row" style="margin-top:6px;">
          <button id="bag2s102-refresh" class="btn">Refresh Command</button>
          <button id="bag2s102-copy" class="btn primary">Copy</button>
          <button id="bag2s102-saveps1" class="btn">Save .ps1</button>
        </div>
        <div class="small" style="margin-top:6px;">
          ${esc(status || "Install deps: pip install -r scripts/requirements-bag2s102.txt")}
        </div>
      </div>
    `;
  }

  function refreshCommandAndValidate() {
    pullStateFromForm();
    const err = validateForm();
    status = err || "";
    const cmdEl = containerEl?.querySelector?.("#bag2s102-cmd");
    if (cmdEl) cmdEl.value = buildCommand(state);
    const msgEl = containerEl?.querySelector?.(".small:last-child");
    if (msgEl) msgEl.textContent = status || "Install deps: pip install -r scripts/requirements-bag2s102.txt";
  }

  async function copyCommand() {
    pullStateFromForm();
    const err = validateForm();
    if (err) {
      status = err;
      render();
      return;
    }
    const cmd = buildCommand(state);
    try {
      await navigator.clipboard.writeText(cmd);
      status = "Command copied to clipboard.";
    } catch {
      status = "Clipboard copy failed; command is shown above.";
    }
    render();
  }

  function savePs1() {
    pullStateFromForm();
    const err = validateForm();
    if (err) {
      status = err;
      render();
      return;
    }
    const cmd = buildCommand(state);
    const script = [
      "$ErrorActionPreference = 'Stop'",
      "",
      "# BAG -> S-102 conversion command",
      cmd,
      "",
    ].join("\n");
    downloadText("run-bag2s102.ps1", script);
    status = "Saved PowerShell script.";
    render();
  }

  function onInput() {
    refreshCommandAndValidate();
  }

  function onChange() {
    refreshCommandAndValidate();
  }

  function onClick(e) {
    if (e.target?.closest?.("#bag2s102-refresh")) {
      refreshCommandAndValidate();
      return;
    }
    if (e.target?.closest?.("#bag2s102-copy")) {
      copyCommand();
      return;
    }
    if (e.target?.closest?.("#bag2s102-saveps1")) {
      savePs1();
    }
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("click", onClick);
    }
    containerEl = el;
    render();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("click", onClick);
  }

  function destroy() {
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onChange);
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshot() {
    return { version: 1, state: { ...state } };
  }

  function restore(data) {
    const s = data?.state;
    if (!s || typeof s !== "object") return;
    state = {
      ...state,
      ...s,
      overwrite: !!s.overwrite,
      validate: s.validate !== false,
      dryRun: !!s.dryRun,
    };
    persist();
    render();
  }

  return { mount, destroy, snapshot, restore };
}

